import React, { ReactNode, useEffect, useReducer } from 'react';
import Pusher, { PresenceChannel } from 'pusher-js';

import { SeverityType } from 'presentations/views/components/atoms/Snackbar';
import { usePusher } from 'presentations/views/components/widgets/MeetingWidget/hooks/pusher';
import { ViewerItem, LiveUser } from 'presentations/api/schema/user';

export interface State {
  viewer: ViewerItem | null;
  uid: string | null;
  url: string | null;
  callUserId: string | null;
  liveViewer: LiveUser | null;
  liveUsers: any;
  message: string | null;
  messageSeverity: SeverityType | null;
  isShowIcon: boolean;
  showingMenu: string | null;
  pusher: Pusher | null;
  channel: PresenceChannel | null;
  messageChannel: PresenceChannel | null;
  scheduledAt: Date | null;
}

const initialState: State = {
  viewer: null,
  uid: null,
  url: null,
  callUserId: null,
  liveViewer: null,
  liveUsers: {},
  message: null,
  messageSeverity: null,
  isShowIcon: true,
  showingMenu: null,
  pusher: null,
  channel: null,
  messageChannel: null,
  scheduledAt: null,
};

export const setViewer = (viewer: ViewerItem | null) =>
  ({
    type: 'SET_VIEWER',
    viewer,
  } as const);
export const setUid = (uid: string | null) =>
  ({
    type: 'SET_UID',
    uid,
  } as const);
export const setUrl = (url: string | null) =>
  ({
    type: 'SET_URL',
    url,
  } as const);

export const setCallUserId = (callUserId: string | null) =>
  ({
    type: 'SET_CALL_USER_ID',
    callUserId,
  } as const);
export const setLiveViewer = (liveViewer: LiveUser | null) =>
  ({
    type: 'SET_LIVE_VIEWER',
    liveViewer,
  } as const);
export const setLiveUsers = (liveUsers: any) =>
  ({
    type: 'SET_LIVE_USERS',
    liveUsers,
  } as const);
export const setMessage = (message: string | null) =>
  ({
    type: 'SET_MESSAGE',
    message,
  } as const);
export const setPusher = (pusher: Pusher | null) =>
  ({
    type: 'SET_PUSHER',
    pusher,
  } as const);
export const setChannel = (channel: PresenceChannel | null) =>
  ({
    type: 'SET_CHANNEL',
    channel,
  } as const);
export const setMessageChannel = (channel: PresenceChannel | null) =>
  ({
    type: 'SET_MESSAGE_CHANNEL',
    channel,
  } as const);
export const setScheduledAt = (scheduledAt: Date | null) =>
  ({
    type: 'SET_SCHEDULED_AT',
    scheduledAt,
  } as const);
export const setMessageSeverity = (messageSeverity: SeverityType) =>
  ({
    type: 'SET_MESSAGE_SEVERITY',
    messageSeverity,
  } as const);
export const setIsShowIcon = (isShowIcon: boolean) =>
  ({
    type: 'SET_IS_SHOW_ICON',
    isShowIcon,
  } as const);
export const setShowingMenu = (showingMenu: string | null) =>
  ({
    type: 'SET_SHOWING_MENU',
    showingMenu,
  } as const);

export type Action =
  | ReturnType<typeof setViewer>
  | ReturnType<typeof setUid>
  | ReturnType<typeof setUrl>
  | ReturnType<typeof setCallUserId>
  | ReturnType<typeof setLiveViewer>
  | ReturnType<typeof setLiveUsers>
  | ReturnType<typeof setMessage>
  | ReturnType<typeof setMessageSeverity>
  | ReturnType<typeof setIsShowIcon>
  | ReturnType<typeof setPusher>
  | ReturnType<typeof setChannel>
  | ReturnType<typeof setMessageChannel>
  | ReturnType<typeof setScheduledAt>
  | ReturnType<typeof setShowingMenu>;

const reducer = (state: State = initialState, action: Action) => {
  // console.debug(action.type, action);
  switch (action.type) {
    case 'SET_VIEWER':
      return { ...state, viewer: action.viewer };
    case 'SET_UID':
      return { ...state, uid: action.uid };
    case 'SET_URL':
      return { ...state, url: action.url };
    case 'SET_CALL_USER_ID':
      return { ...state, callUserId: action.callUserId };
    case 'SET_LIVE_VIEWER':
      return { ...state, liveViewer: action.liveViewer };
    case 'SET_LIVE_USERS':
      return { ...state, liveUsers: action.liveUsers };
    case 'SET_MESSAGE':
      return { ...state, message: action.message };
    case 'SET_PUSHER':
      return { ...state, pusher: action.pusher };
    case 'SET_CHANNEL':
      return { ...state, channel: action.channel };
    case 'SET_MESSAGE_CHANNEL':
      return { ...state, messageChannel: action.channel };
    case 'SET_SCHEDULED_AT':
      return { ...state, scheduledAt: action.scheduledAt };
    case 'SET_MESSAGE_SEVERITY':
      return { ...state, messageSeverity: action.messageSeverity };
    case 'SET_IS_SHOW_ICON':
      return { ...state, isShowIcon: action.isShowIcon };
    case 'SET_SHOWING_MENU':
      return { ...state, showingMenu: action.showingMenu };
  }
};

const MeetingWidgetContext = React.createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: initialState,
  dispatch: () => {},
});

const MeetingWidgetProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Enable pusher logging - don't include this in production
  // Pusher.logToConsole = true;

  const pusher = usePusher({
    state,
    dispatch,
  });

  useEffect(() => {
    return function cleanUp() {
      pusher.actions.disconnect();
    };
  }, []);

  useEffect(() => {
    if (state.uid) {
      pusher.actions.disconnect();
      pusher.actions.connect();
    } else {
      pusher.actions.disconnect();
    }
  }, [state.uid]);

  useEffect(() => {
    if (state.uid || !state.messageChannel) return;

    state.messageChannel?.unsubscribe();
    dispatch(setMessageChannel(null));
  }, [state.uid, state.messageChannel]);

  return (
    <MeetingWidgetContext.Provider value={{ state, dispatch }}>
      {children}
    </MeetingWidgetContext.Provider>
  );
};

export { MeetingWidgetContext, MeetingWidgetProvider };
